import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../components/ConfirmModal';
import toast from 'react-hot-toast';
import { leadsAPI, userAPI } from '../services/api';

/* ── Stage definitions ────────────────────────────────────── */
const STAGES = [
  { key: 'lead',        label: 'New Lead',     color: '#94a3b8', bg: '#f1f5f9' },
  { key: 'qualified',   label: 'Qualified',    color: '#3b82f6', bg: '#eff6ff' },
  { key: 'proposal',    label: 'Proposal',     color: 'var(--pm-secondary)', bg: '#f5f3ff' },
  { key: 'negotiation', label: 'Negotiation',  color: '#f59e0b', bg: '#fffbeb' },
  { key: 'won',         label: 'Won ✓',        color: '#22c55e', bg: '#f0fdf4' },
  { key: 'lost',        label: 'Lost ✗',       color: '#ef4444', bg: '#fef2f2' },
];

const PRIORITY_COLORS = {
  low:    { color: '#166534', bg: '#dcfce7' },
  medium: { color: '#92400e', bg: '#fef3c7' },
  high:   { color: '#991b1b', bg: '#fee2e2' },
};

const CURRENCIES = ['INR', 'USD', 'GBP', 'EUR'];

const EMPTY_FORM = {
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
  dealValue: '',
  currency: 'INR',
  stage: 'lead',
  probability: '',
  priority: 'medium',
  source: '',
  expectedCloseDate: '',
  notes: '',
  assignedTo: '',
};

/* ── Currency symbol helper ───────────────────────────────── */
function currencySymbol(c) {
  const map = { INR: '₹', USD: '$', GBP: '£', EUR: '€' };
  return map[c] || c;
}

/* ── Format deal value ────────────────────────────────────── */
function formatValue(value, currency = 'INR') {
  if (!value && value !== 0) return '—';
  const sym = currencySymbol(currency);
  const num = Number(value);
  if (currency === 'INR') {
    return sym + num.toLocaleString('en-IN');
  }
  return sym + num.toLocaleString('en-US');
}

/* ── Stage index helpers ──────────────────────────────────── */
function stageIndex(key) {
  return STAGES.findIndex(s => s.key === key);
}

/* ── Date format ──────────────────────────────────────────── */
function fmtDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' });
}

/* ═══════════════════════════════════════════════════════════
   Main Component
═══════════════════════════════════════════════════════════ */
export default function CRM() {
  useAuth(); // ensure auth context is loaded
  const confirm = useConfirm();

  /* State */
  const [leads,    setLeads]    = useState([]);
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [viewMode, setViewMode] = useState('pipeline'); // 'pipeline' | 'list'
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [saving,   setSaving]   = useState(false);
  const [users,    setUsers]    = useState([]);
  const [movingId, setMovingId] = useState(null);

  /* ── Data fetchers ───────────────────────────────────────── */
  const fetchLeads = useCallback(async () => {
    try {
      const res = await leadsAPI.getAll();
      setLeads(res.data?.data || res.data || []);
    } catch (err) {
      toast.error('Failed to load leads');
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await leadsAPI.getStats();
      setStats(res.data?.data || res.data || null);
    } catch {
      /* silent */
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await userAPI.getAll({ role: 'admin' });
      const admins = res.data?.data || res.data || [];
      const res2 = await userAPI.getAll({ role: 'team' });
      const team = res2.data?.data || res2.data || [];
      setUsers([...admins, ...team]);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchStats(), fetchUsers()]);
      setLoading(false);
    };
    init();
  }, [fetchLeads, fetchStats, fetchUsers]);

  /* ── Refresh helper ──────────────────────────────────────── */
  const refresh = useCallback(async () => {
    await Promise.all([fetchLeads(), fetchStats()]);
  }, [fetchLeads, fetchStats]);

  /* ── Form helpers ────────────────────────────────────────── */
  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModal(true);
  };

  const openEdit = (lead) => {
    setEditing(lead);
    setForm({
      companyName:       lead.companyName || '',
      contactName:       lead.contactName || '',
      email:             lead.email || '',
      phone:             lead.phone || '',
      dealValue:         lead.dealValue ?? '',
      currency:          lead.currency || 'INR',
      stage:             lead.stage || 'lead',
      probability:       lead.probability ?? '',
      priority:          lead.priority || 'medium',
      source:            lead.source || '',
      expectedCloseDate: lead.expectedCloseDate ? lead.expectedCloseDate.slice(0, 10) : '',
      notes:             lead.notes || '',
      assignedTo:        lead.assignedTo?.id || lead.assignedTo?._id || '',
    });
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  /* ── Submit ──────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.companyName.trim()) { toast.error('Company name is required'); return; }
    if (!form.contactName.trim()) { toast.error('Contact name is required'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        dealValue:   form.dealValue  !== '' ? Number(form.dealValue)  : undefined,
        probability: form.probability !== '' ? Number(form.probability) : undefined,
      };
      if (editing) {
        await leadsAPI.update(editing._id, payload);
        toast.success('Lead updated');
      } else {
        await leadsAPI.create(payload);
        toast.success('Lead created');
      }
      closeModal();
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save lead');
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ──────────────────────────────────────────────── */
  const handleDelete = async (lead) => {
    const ok = await confirm({
      title:   'Delete Lead',
      message: `Delete lead for "${lead.companyName}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger:  true,
    });
    if (!ok) return;
    try {
      await leadsAPI.delete(lead._id);
      toast.success('Lead deleted');
      await refresh();
    } catch {
      toast.error('Failed to delete lead');
    }
  };

  /* ── Stage move ──────────────────────────────────────────── */
  const moveStage = async (lead, direction) => {
    const idx = stageIndex(lead.stage);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= STAGES.length) return;
    const newStage = STAGES[newIdx].key;
    setMovingId(lead._id);
    try {
      await leadsAPI.update(lead._id, { stage: newStage });
      await refresh();
    } catch {
      toast.error('Failed to move lead');
    } finally {
      setMovingId(null);
    }
  };

  const changeStage = async (lead, newStage) => {
    if (lead.stage === newStage) return;
    setMovingId(lead._id);
    try {
      await leadsAPI.update(lead._id, { stage: newStage });
      await refresh();
    } catch {
      toast.error('Failed to update stage');
    } finally {
      setMovingId(null);
    }
  };

  /* ── Derived data ────────────────────────────────────────── */
  const leadsByStage = STAGES.reduce((acc, s) => {
    acc[s.key] = leads.filter(l => l.stage === s.key);
    return acc;
  }, {});

  const totalPipelineValue = leads
    .filter(l => l.stage !== 'lost')
    .reduce((sum, l) => sum + (Number(l.dealValue) || 0), 0);

  const wonLeads = leads.filter(l => l.stage === 'won').length;

  /* ── Stats cards data ────────────────────────────────────── */
  const statCards = [
    {
      label: 'Total Leads',
      value: stats?.total ?? leads.length,
      color: 'var(--pm-primary)',
      bg:    '#eef2ff',
      icon:  '👥',
    },
    {
      label: 'Pipeline Value',
      value: '₹' + totalPipelineValue.toLocaleString('en-IN'),
      color: '#22c55e',
      bg:    '#f0fdf4',
      icon:  '💰',
    },
    {
      label: 'Won Leads',
      value: wonLeads,
      color: '#22c55e',
      bg:    '#f0fdf4',
      icon:  '✓',
    },
    {
      label: 'Conversion Rate',
      value: stats?.conversionRate != null
        ? stats.conversionRate.toFixed(1) + '%'
        : leads.length > 0
          ? ((wonLeads / leads.length) * 100).toFixed(1) + '%'
          : '0%',
      color: '#f59e0b',
      bg:    '#fffbeb',
      icon:  '📈',
    },
  ];

  /* ═══════════════════════════════════════════════════════════
     Render
  ═══════════════════════════════════════════════════════════ */
  return (
    <div style={{ fontFamily: "'Sora', sans-serif", minHeight: '100vh', background: '#f8fafc', padding: '28px 24px' }}>

      {/* ── Header ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>CRM Pipeline</h1>
          <p style={{ color: '#64748b', fontSize: 14, marginTop: 4, marginBottom: 0 }}>
            Track and manage your sales leads through stages
          </p>
        </div>
        <button
          onClick={openAdd}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'var(--pm-primary)', color: '#fff',
            border: 'none', borderRadius: 10,
            padding: '10px 20px', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'Sora', sans-serif",
            boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
            transition: 'background 0.2s',
          }}
          onMouseOver={e => e.currentTarget.style.background = '#4f46e5'}
          onMouseOut={e => e.currentTarget.style.background = 'var(--pm-primary)'}
        >
          + Add Lead
        </button>
      </div>

      {/* ── Stats Row ──────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
        {statCards.map(card => (
          <div key={card.label} style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
            padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10,
              background: card.bg, display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 20, flexShrink: 0,
            }}>
              {card.icon}
            </div>
            <div>
              <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500, marginBottom: 2 }}>{card.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: card.color }}>{card.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── View Toggle ────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 20 }}>
        {['pipeline', 'list'].map(v => (
          <button
            key={v}
            onClick={() => setViewMode(v)}
            style={{
              padding: '7px 18px', borderRadius: 8, border: 'none',
              fontFamily: "'Sora', sans-serif", fontSize: 13, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s',
              background: viewMode === v ? 'var(--pm-primary)' : '#f1f5f9',
              color: viewMode === v ? '#fff' : '#64748b',
            }}
          >
            {v === 'pipeline' ? 'Pipeline' : 'List'}
          </button>
        ))}
        <span style={{ marginLeft: 8, color: '#94a3b8', fontSize: 13 }}>
          {leads.length} lead{leads.length !== 1 ? 's' : ''} total
        </span>
      </div>

      {/* ── Loading ────────────────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#94a3b8', fontSize: 15 }}>
          Loading leads...
        </div>
      ) : viewMode === 'pipeline' ? (
        <PipelineView
          leadsByStage={leadsByStage}
          movingId={movingId}
          onEdit={openEdit}
          onDelete={handleDelete}
          onMoveStage={moveStage}
          onChangeStage={changeStage}
        />
      ) : (
        <ListView
          leads={leads}
          onEdit={openEdit}
          onDelete={handleDelete}
        />
      )}

      {/* ── Add/Edit Modal ─────────────────────────────────── */}
      {modal && (
        <LeadModal
          editing={editing}
          form={form}
          saving={saving}
          users={users}
          onChange={handleFormChange}
          onSubmit={handleSubmit}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Pipeline View (Kanban columns)
═══════════════════════════════════════════════════════════ */
function PipelineView({ leadsByStage, movingId, onEdit, onDelete, onMoveStage, onChangeStage }) {
  return (
    <div style={{
      display: 'flex', gap: 14, overflowX: 'auto',
      paddingBottom: 16,
      alignItems: 'flex-start',
    }}>
      {STAGES.map(stage => {
        const stageLeads = leadsByStage[stage.key] || [];
        const stageTotal = stageLeads.reduce((s, l) => s + (Number(l.dealValue) || 0), 0);

        return (
          <div
            key={stage.key}
            style={{
              minWidth: 240, maxWidth: 260, flex: '0 0 240px',
              background: stage.bg,
              border: `1px solid ${stage.color}30`,
              borderRadius: 12,
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Column header */}
            <div style={{
              padding: '12px 14px 10px',
              borderBottom: `2px solid ${stage.color}40`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: stage.color }}>{stage.label}</span>
                <span style={{
                  background: stage.color, color: '#fff',
                  borderRadius: 20, padding: '1px 8px',
                  fontSize: 11, fontWeight: 700,
                }}>
                  {stageLeads.length}
                </span>
              </div>
              {stageTotal > 0 && (
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                  ₹{stageTotal.toLocaleString('en-IN')}
                </div>
              )}
            </div>

            {/* Cards */}
            <div style={{ padding: '10px 10px', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 80 }}>
              {stageLeads.length === 0 && (
                <div style={{ textAlign: 'center', padding: '18px 0', color: '#94a3b8', fontSize: 12 }}>
                  No leads
                </div>
              )}
              {stageLeads.map(lead => (
                <LeadCard
                  key={lead._id}
                  lead={lead}
                  moving={movingId === lead._id}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onMoveStage={onMoveStage}
                  onChangeStage={onChangeStage}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Lead Card
═══════════════════════════════════════════════════════════ */
function LeadCard({ lead, moving, onEdit, onDelete, onMoveStage, onChangeStage }) {
  const [hovered, setHovered] = useState(false);
  const [showStageSelect, setShowStageSelect] = useState(false);

  const idx = stageIndex(lead.stage);
  const priColors = PRIORITY_COLORS[lead.priority] || PRIORITY_COLORS.medium;
  const assignedInitial = lead.assignedTo?.name?.charAt(0)?.toUpperCase() || '?';
  const closeDate = fmtDate(lead.expectedCloseDate);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setShowStageSelect(false); }}
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        padding: '10px 12px',
        opacity: moving ? 0.5 : 1,
        transition: 'box-shadow 0.15s, opacity 0.2s',
        boxShadow: hovered ? '0 4px 14px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.06)',
        cursor: 'default',
        position: 'relative',
      }}
    >
      {/* Company + Priority */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}>
          {lead.companyName}
        </span>
        <span style={{
          fontSize: 10, fontWeight: 700, borderRadius: 6,
          padding: '2px 6px',
          color: priColors.color, background: priColors.bg,
          flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          {lead.priority || 'med'}
        </span>
      </div>

      {/* Contact */}
      {lead.contactName && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>{lead.contactName}</div>
      )}

      {/* Deal value */}
      {lead.dealValue ? (
        <div style={{ fontSize: 13, fontWeight: 700, color: '#16a34a', marginTop: 6 }}>
          {formatValue(lead.dealValue, lead.currency)}
        </div>
      ) : null}

      {/* Close date + Assigned */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
        {closeDate ? (
          <span style={{ fontSize: 10, color: '#94a3b8' }}>📅 {closeDate}</span>
        ) : <span />}
        {lead.assignedTo?.name && (
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            background: 'var(--pm-primary)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 700,
          }}>
            {assignedInitial}
          </div>
        )}
      </div>

      {/* Hover actions */}
      {hovered && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9',
          gap: 4,
        }}>
          {/* Prev/Next arrows */}
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              disabled={idx === 0 || moving}
              onClick={() => onMoveStage(lead, -1)}
              title="Previous stage"
              style={{
                width: 26, height: 26, borderRadius: 6, border: '1px solid #e2e8f0',
                background: idx === 0 ? '#f8fafc' : '#fff', cursor: idx === 0 ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: idx === 0 ? '#cbd5e1' : 'var(--pm-primary)', fontSize: 13, fontWeight: 700,
                fontFamily: "'Sora', sans-serif",
              }}
            >
              ‹
            </button>
            <button
              disabled={idx === STAGES.length - 1 || moving}
              onClick={() => onMoveStage(lead, 1)}
              title="Next stage"
              style={{
                width: 26, height: 26, borderRadius: 6, border: '1px solid #e2e8f0',
                background: idx === STAGES.length - 1 ? '#f8fafc' : '#fff',
                cursor: idx === STAGES.length - 1 ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: idx === STAGES.length - 1 ? '#cbd5e1' : 'var(--pm-primary)', fontSize: 13, fontWeight: 700,
                fontFamily: "'Sora', sans-serif",
              }}
            >
              ›
            </button>
            <button
              onClick={() => setShowStageSelect(prev => !prev)}
              title="Move to stage"
              style={{
                height: 26, padding: '0 8px', borderRadius: 6, border: '1px solid #e2e8f0',
                background: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--pm-primary)', fontSize: 10, fontWeight: 600,
                fontFamily: "'Sora', sans-serif",
              }}
            >
              Move to ▾
            </button>
          </div>

          {/* Edit / Delete */}
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={() => onEdit(lead)}
              title="Edit"
              style={{
                width: 26, height: 26, borderRadius: 6, border: '1px solid #e2e8f0',
                background: '#fff', cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', color: 'var(--pm-primary)', fontSize: 13,
                fontFamily: "'Sora', sans-serif",
              }}
            >
              ✎
            </button>
            <button
              onClick={() => onDelete(lead)}
              title="Delete"
              style={{
                width: 26, height: 26, borderRadius: 6, border: '1px solid #fee2e2',
                background: '#fff', cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', color: '#ef4444', fontSize: 13,
                fontFamily: "'Sora', sans-serif",
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Stage select dropdown */}
      {showStageSelect && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          padding: 6,
          marginTop: 4,
          transform: 'translateY(calc(100% + 4px))',
        }}>
          {STAGES.map(s => (
            <button
              key={s.key}
              onClick={() => { onChangeStage(lead, s.key); setShowStageSelect(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '6px 10px', border: 'none', borderRadius: 6,
                background: lead.stage === s.key ? s.bg : 'transparent',
                color: lead.stage === s.key ? s.color : '#334155',
                fontSize: 12, fontWeight: lead.stage === s.key ? 700 : 500,
                cursor: 'pointer', fontFamily: "'Sora', sans-serif",
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   List View
═══════════════════════════════════════════════════════════ */
function ListView({ leads, onEdit, onDelete }) {
  if (leads.length === 0) {
    return (
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
        padding: '60px 0', textAlign: 'center', color: '#94a3b8', fontSize: 15,
      }}>
        No leads found. Click "Add Lead" to create your first one.
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              {['Company', 'Contact', 'Stage', 'Value', 'Priority', 'Source', 'Close Date', 'Assigned To', 'Actions'].map(h => (
                <th key={h} style={{
                  padding: '11px 16px', textAlign: 'left',
                  fontSize: 12, fontWeight: 600, color: '#64748b',
                  whiteSpace: 'nowrap',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.map((lead, i) => {
              const stage    = STAGES.find(s => s.key === lead.stage) || STAGES[0];
              const priC     = PRIORITY_COLORS[lead.priority] || PRIORITY_COLORS.medium;
              const closeDate = fmtDate(lead.expectedCloseDate);

              return (
                <tr
                  key={lead._id}
                  style={{
                    borderBottom: i < leads.length - 1 ? '1px solid #f1f5f9' : 'none',
                    transition: 'background 0.1s',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseOut={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '12px 16px', fontWeight: 700, color: '#1e293b' }}>
                    {lead.companyName}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#475569' }}>
                    <div>{lead.contactName}</div>
                    {lead.email && <div style={{ fontSize: 11, color: '#94a3b8' }}>{lead.email}</div>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      color: stage.color, background: stage.bg,
                      border: `1px solid ${stage.color}40`,
                    }}>
                      {stage.label}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: '#16a34a' }}>
                    {lead.dealValue ? formatValue(lead.dealValue, lead.currency) : '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                      color: priC.color, background: priC.bg,
                      textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                      {lead.priority || '—'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>
                    {lead.source || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#64748b', whiteSpace: 'nowrap' }}>
                    {closeDate || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>
                    {lead.assignedTo?.name || '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => onEdit(lead)}
                        style={{
                          padding: '5px 12px', borderRadius: 6, border: '1px solid #e2e8f0',
                          background: '#fff', color: 'var(--pm-primary)', fontSize: 12, fontWeight: 600,
                          cursor: 'pointer', fontFamily: "'Sora', sans-serif",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onDelete(lead)}
                        style={{
                          padding: '5px 12px', borderRadius: 6, border: '1px solid #fee2e2',
                          background: '#fff', color: '#ef4444', fontSize: 12, fontWeight: 600,
                          cursor: 'pointer', fontFamily: "'Sora', sans-serif",
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Add / Edit Modal
═══════════════════════════════════════════════════════════ */
function LeadModal({ editing, form, saving, users, onChange, onSubmit, onClose }) {
  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: '1px solid #e2e8f0', fontSize: 13,
    fontFamily: "'Sora', sans-serif", color: '#1e293b',
    outline: 'none', boxSizing: 'border-box',
    background: '#fff',
  };
  const labelStyle = {
    fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 16,
        width: '100%', maxWidth: 680,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        {/* Modal header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9',
        }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>
            {editing ? 'Edit Lead' : 'Add New Lead'}
          </h2>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0',
              background: '#f8fafc', color: '#64748b', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
              fontFamily: "'Sora', sans-serif",
            }}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} style={{ padding: '20px 24px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px' }}>

            {/* Company Name */}
            <div>
              <label style={labelStyle}>Company Name *</label>
              <input
                name="companyName" value={form.companyName} onChange={onChange}
                required placeholder="Acme Corp"
                style={inputStyle}
              />
            </div>

            {/* Contact Name */}
            <div>
              <label style={labelStyle}>Contact Name *</label>
              <input
                name="contactName" value={form.contactName} onChange={onChange}
                required placeholder="John Doe"
                style={inputStyle}
              />
            </div>

            {/* Email */}
            <div>
              <label style={labelStyle}>Email</label>
              <input
                name="email" value={form.email} onChange={onChange}
                type="email" placeholder="john@acme.com"
                style={inputStyle}
              />
            </div>

            {/* Phone */}
            <div>
              <label style={labelStyle}>Phone</label>
              <input
                name="phone" value={form.phone} onChange={onChange}
                placeholder="+91 98765 43210"
                style={inputStyle}
              />
            </div>

            {/* Deal Value + Currency */}
            <div>
              <label style={labelStyle}>Deal Value</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  name="dealValue" value={form.dealValue} onChange={onChange}
                  type="number" min="0" placeholder="500000"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <select
                  name="currency" value={form.currency} onChange={onChange}
                  style={{ ...inputStyle, width: 80, flex: '0 0 80px' }}
                >
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Stage */}
            <div>
              <label style={labelStyle}>Stage</label>
              <select name="stage" value={form.stage} onChange={onChange} style={inputStyle}>
                {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>

            {/* Probability */}
            <div>
              <label style={labelStyle}>Probability %</label>
              <input
                name="probability" value={form.probability} onChange={onChange}
                type="number" min="0" max="100" placeholder="50"
                style={inputStyle}
              />
            </div>

            {/* Priority */}
            <div>
              <label style={labelStyle}>Priority</label>
              <select name="priority" value={form.priority} onChange={onChange} style={inputStyle}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            {/* Source */}
            <div>
              <label style={labelStyle}>Source</label>
              <input
                name="source" value={form.source} onChange={onChange}
                placeholder="referral, website, cold-call, linkedin..."
                style={inputStyle}
              />
            </div>

            {/* Expected Close Date */}
            <div>
              <label style={labelStyle}>Expected Close Date</label>
              <input
                name="expectedCloseDate" value={form.expectedCloseDate} onChange={onChange}
                type="date"
                style={inputStyle}
              />
            </div>

            {/* Assigned To */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Assigned To</label>
              <select name="assignedTo" value={form.assignedTo} onChange={onChange} style={inputStyle}>
                <option value="">— Unassigned —</option>
                {users.map(u => (
                  <option key={u._id} value={u._id}>{u.name} ({u.role})</option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Notes</label>
              <textarea
                name="notes" value={form.notes} onChange={onChange}
                rows={3}
                placeholder="Add any notes about this lead..."
                style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
              />
            </div>
          </div>

          {/* Footer buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <button
              type="button" onClick={onClose}
              style={{
                padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0',
                background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: "'Sora', sans-serif",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '9px 24px', borderRadius: 8, border: 'none',
                background: saving ? '#a5b4fc' : 'var(--pm-primary)', color: '#fff',
                fontSize: 13, fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: "'Sora', sans-serif",
                boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
              }}
            >
              {saving ? 'Saving...' : editing ? 'Update Lead' : 'Create Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
